import React from "react";
import { FiCheck, FiDollarSign, FiFileText, FiShare2, FiUser } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import * as S from "./style";
import { copyUserProfileLink } from "../../Utils";

export interface CreatorOnboardingState {
  profileComplete: boolean;
  membershipComplete: boolean;
  firstPostComplete: boolean;
  shareComplete: boolean;
  username?: string;
}

interface Props {
  state: CreatorOnboardingState;
  onCreatePost?: () => void;
}

const CreatorOnboardingChecklist: React.FC<Props> = ({ state, onCreatePost }) => {
  const navigate = useNavigate();

  const steps = [
    {
      title: "Set up your profile",
      description: "Add your DP, cover & bio so fans recognize you",
      complete: state.profileComplete,
      icon: <FiUser />,
      action: () => navigate("/profile/edit"),
    },
    {
      title: "Set up your membership",
      description: "Choose your subscription & paid DM pricing",
      complete: state.membershipComplete,
      icon: <FiDollarSign />,
      action: () => navigate("/membership/pricing"),
    },
    {
      title: "Create your first post",
      description: "Introduce yourself and what fans will get",
      complete: state.firstPostComplete,
      icon: <FiFileText />,
      action: () => {
        if (onCreatePost) onCreatePost();
      },
    },
    {
      title: "Share your profile",
      description: "Start bringing in your audience",
      complete: state.shareComplete,
      icon: <FiShare2 />,
      action: () => {
        if (state.username) copyUserProfileLink(state.username);
      },
    },
  ];

  const completedCount = steps.filter((step) => step.complete).length;

  if (completedCount === steps.length) return null;

  return (
    <S.ChecklistWrapper>
      <S.Header>
        <div>
          <h2>Start your premium community 🚀</h2>
          <p>Set up your profile and start earning</p>
        </div>
        <S.Progress>{completedCount}/4 completed</S.Progress>
      </S.Header>

      <S.StepList>
        {steps.map((step) => (
          <S.StepButton
            key={step.title}
            type="button"
            onClick={step.complete ? undefined : step.action}
            $complete={step.complete}
          >
            <S.IconBox $complete={step.complete}>
              {step.complete ? <FiCheck /> : step.icon}
            </S.IconBox>
            <S.StepText>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </S.StepText>
          </S.StepButton>
        ))}
      </S.StepList>

      <S.Footer>And let it roll ✨</S.Footer>
    </S.ChecklistWrapper>
  );
};

export default CreatorOnboardingChecklist;
